import React from 'react';
import classNames from 'classnames';
import styles from './styles.module.scss';
import ErrorView from './ErrorView';
import { useTypedDispatch, useTypedSelector } from '@profile/src/redux/utils';
import { makeSelectAmount } from './selectors';
import { exchangeCurrency } from '@profile/src/redux/actions';
import { formatErrorMessage } from './utils';
import iCThetanCoin from 'assets/img/thetan_coin_V2.png';
import icThetanGem from 'assets/img/thetan_gem_V2.png';
import icArrowDown from 'assets/svg/arrow_down_V2.svg';
import { AppError, EipError } from '@profile/src/redux/types';
import { CurrencyType, formatCurrency, normalNumberToSystemCurrency } from '@wolffun/system-currency';
import { MainCurrency } from '../../config';
import { logCoinChangeMarket } from '@profile/src/utils/analytic';
import { BN } from 'bn.js';
import Modal from '@profile/src/Components/PopupModal_V2';
import { ellipsisEnd } from '@profile/src/utils/text';
import Button from '@profile/src/Components/Button_V2';
import useCheckAllowance from '@profile/src/hooks/useCheckAllowance';
import { DEPOSIT_TOKEN_CONTRACT_ADDRESS } from '@profile/src/constants';
const MIN_DEPOSIT_VALUE = 1;
const coinTypeMap = {
  [MainCurrency.THETAN_COIN]: {
    currencyFrom: 'THC',
    currencyTo: 'THC',
    nameFrom: 'Thetan Coin',
    nameTo: 'Ingame Thetan Coin',
    image: iCThetanCoin,
    type: CurrencyType.THC
  },
  [MainCurrency.THETAN_GEM]: {
    currencyFrom: 'THG',
    currencyTo: 'THG',
    nameFrom: 'Thetan Gem',
    nameTo: 'Ingame Thetan Gem',
    image: icThetanGem,
    type: CurrencyType.THG
  }
};

const ExchangeModal = ({
  onClose,
  onSuccess,
  coinType
}) => {
  const dispatch = useTypedDispatch();
  const coinInfo = coinTypeMap[coinType];
  const loading = useTypedSelector(state => state.exchangeCurrency.loading);
  const [price, setPrice] = React.useState(0);
  const [error, setError] = React.useState(AppError.NO_ERROR);
  const exchangePrice = React.useMemo(() => {
    return normalNumberToSystemCurrency(price, coinInfo.type);
  }, [coinInfo.type, price]);
  const {
    approveUseBalance,
    isEnoughAllowance,
    isProcessing
  } = useCheckAllowance({
    contractAddress: DEPOSIT_TOKEN_CONTRACT_ADDRESS,
    thresholdPrice: exchangePrice
  });
  const balance = useTypedSelector(state => makeSelectAmount(state, coinType));
  const thcBalance = useTypedSelector(state => state.connectWallet.thcBalance);
  const thgBalance = useTypedSelector(state => state.connectWallet.thgBalance);
  const userId = useTypedSelector(state => {
    var _state$userProfile$us;

    return (_state$userProfile$us = state.userProfile.user) === null || _state$userProfile$us === void 0 ? void 0 : _state$userProfile$us.id;
  });

  const onClickConfirm = async () => {
    const {
      error
    } = await dispatch(exchangeCurrency(exchangePrice));

    if (error) {
      setError(error);
    } else {
      onClose();
      onSuccess();

      if (thcBalance && thgBalance && userId) {
        logCoinChangeMarket({
          THC: thcBalance,
          THG: thgBalance,
          price: exchangePrice,
          userId,
          source: 'MarketExchange'
        });
      }
    }
  };

  React.useEffect(() => {
    if (!balance) {
      setError(AppError.NO_ERROR);
      return;
    }

    const priceCurrency = normalNumberToSystemCurrency(price, balance.token);
    const priceBN = new BN(priceCurrency.value);
    const balanceBN = new BN(balance.value);

    if (price > 0 && price < MIN_DEPOSIT_VALUE) {
      setError(AppError.MIN_DEPOSIT_EXCEEDED);
      return;
    }

    if (priceBN.gt(balanceBN)) switch (coinType) {
      case MainCurrency.THETAN_COIN:
        setError(AppError.NOT_ENOUGH_THC);
        break;

      case MainCurrency.THETAN_GEM:
        setError(AppError.NOT_ENOUGH_THG);
        break;

      default:
        setError(AppError.UNNKOWN);
        break;
    } else setError(AppError.NO_ERROR);
  }, [balance, price]);
  return /*#__PURE__*/React.createElement(Modal, {
    header: "Deposit",
    onClose: onClose
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.desc
  }, "Exchange ", coinInfo.nameFrom, " to ", coinInfo.nameTo), /*#__PURE__*/React.createElement("span", {
    className: classNames(styles.desc, styles.amount)
  }, "Your ", coinInfo.currencyFrom, ": ", balance && formatCurrency(balance)), /*#__PURE__*/React.createElement("div", {
    className: styles.inputView
  }, /*#__PURE__*/React.createElement("label", {
    className: styles.labelInput,
    htmlFor: "amount-converted"
  }, "Amount"), /*#__PURE__*/React.createElement("div", {
    className: styles.priceRow
  }, /*#__PURE__*/React.createElement("input", {
    id: "amount-converted",
    placeholder: "Amount",
    className: classNames(styles.input, styles.price),
    type: "number",
    min: 0,
    onChange: e => {
      const value = Number(e.target.value) || 0;
      setPrice(value);
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.unit
  }, /*#__PURE__*/React.createElement("img", {
    draggable: false,
    src: coinInfo.image,
    className: styles.coin
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, coinInfo.currencyFrom))), /*#__PURE__*/React.createElement("div", {
    className: styles.arrow
  }, /*#__PURE__*/React.createElement("img", {
    src: icArrowDown
  })), /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.priceRow, styles.priceEstimated)
  }, /*#__PURE__*/React.createElement("span", {
    className: classNames(styles.convertedPrice)
  }, ellipsisEnd(price.toString(), 15)), /*#__PURE__*/React.createElement("div", {
    className: styles.unit
  }, /*#__PURE__*/React.createElement("img", {
    draggable: false,
    src: coinInfo.image,
    className: styles.coin
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, coinInfo.currencyTo)))), !!error && /*#__PURE__*/React.createElement(ErrorView, {
    className: styles.error,
    title: formatErrorMessage(error, {
      minDeposit: `${MIN_DEPOSIT_VALUE} ${coinInfo.currencyFrom}`
    })
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.footer
  }, isEnoughAllowance ? /*#__PURE__*/React.createElement(Button, {
    disabled: loading || price <= 0 || !!error,
    onClick: onClickConfirm,
    className: styles.btn,
    type: "success",
    title: loading || isProcessing ? 'Processing...' : 'Deposit'
  }) : /*#__PURE__*/React.createElement(Button, {
    disabled: isProcessing || loading || price <= 0 || !!error,
    onClick: async () => {
      const {
        error
      } = await approveUseBalance();

      if (error !== EipError.NO_ERROR) {
        setError(error);
      }
    },
    className: styles.btn,
    type: "success",
    title: isProcessing ? 'Processing...' : 'Approve'
  })));
};

export default ExchangeModal;